import { SDK_VERSION } from "@firebase/app";
import { app, auth } from "./firebase";

/**
 * Firebase Storage resumable upload that reads the file one piece at a time.
 *
 * The Storage SDK needs the whole file as a Blob. A video picked with the
 * native gallery picker is a file on the device instead (lib/native-media-picker.ts),
 * and loading all of it into the web view could use up to 1 GB of memory, so
 * this speaks the same upload protocol as the SDK (same URLs, headers and
 * security rules) but only holds one piece in memory at a time.
 */

// Must be a multiple of 256 KiB (protocol requirement) except for the last piece.
const CHUNK_BYTES = 8 * 1024 * 1024;
const MAX_ATTEMPTS = 8;

export interface ResumableUploadOptions {
  storagePath: string;
  size: number;
  contentType: string;
  cacheControl?: string;
  customMetadata?: Record<string, string>;
  /** Returns bytes [start, end) of the file. */
  readChunk: (start: number, end: number) => Promise<Blob>;
  /** Total bytes the server has received so far. */
  onProgress: (bytesSent: number) => void;
  signal: AbortSignal;
}

export interface ResumableUploadResult {
  size: number;
}

type CodedError = Error & { code: string };

function codedError(code: string, message = code): CodedError {
  return Object.assign(new Error(message), { code });
}

function storageBaseUrl(bucket: string): string {
  const host =
    import.meta.env.VITE_USE_EMULATORS === "true"
      ? "http://127.0.0.1:9199"
      : "https://firebasestorage.googleapis.com";
  return `${host}/v0/b/${encodeURIComponent(bucket)}/o`;
}

async function authHeaders(forceRefresh = false): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw codedError("storage/unauthenticated", "Niste prijavljeni.");
  const headers: Record<string, string> = {
    Authorization: `Firebase ${await user.getIdToken(forceRefresh)}`,
    "X-Firebase-Storage-Version": `webjs/${SDK_VERSION}`,
  };
  if (app.options.appId) headers["X-Firebase-GMPID"] = app.options.appId;
  return headers;
}

interface XhrResult {
  status: number;
  header: (name: string) => string | null;
  text: string;
}

function send(
  url: string,
  headers: Record<string, string>,
  body: Blob | string | null,
  signal: AbortSignal,
  onUploadProgress?: (loaded: number) => void,
): Promise<XhrResult> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(codedError("storage/canceled"));
      return;
    }
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    signal.addEventListener("abort", onAbort);
    const done = () => signal.removeEventListener("abort", onAbort);
    xhr.open("POST", url);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    if (onUploadProgress) xhr.upload.onprogress = (e) => onUploadProgress(e.loaded);
    xhr.onload = () => {
      done();
      resolve({
        status: xhr.status,
        header: (name) => {
          try {
            return xhr.getResponseHeader(name);
          } catch {
            return null;
          }
        },
        text: xhr.responseText,
      });
    };
    xhr.onerror = () => {
      done();
      reject(codedError("nn/network"));
    };
    xhr.onabort = () => {
      done();
      reject(codedError("storage/canceled"));
    };
    xhr.send(body);
  });
}

function isRetryable(error: unknown): boolean {
  const code = (error as { code?: string })?.code;
  return code === "nn/network" || code === "nn/server";
}

function checkStatus(res: XhrResult) {
  if (res.status >= 200 && res.status < 300) return;
  if (res.status === 401) throw codedError("nn/auth-expired");
  if (res.status === 403) throw codedError("storage/unauthorized");
  if (res.status === 408 || res.status === 429 || res.status >= 500 || res.status === 0) {
    throw codedError("nn/server", `Storage ${res.status}`);
  }
  throw codedError("storage/unknown", `Firebase Storage ${res.status}: ${res.text.slice(0, 200)}`);
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        reject(codedError("storage/canceled"));
      },
      { once: true },
    );
  });
}

/** Runs `request`, retrying network/server errors and refreshing an expired login. */
async function withRetries<T>(
  signal: AbortSignal,
  request: (headers: Record<string, string>) => Promise<T>,
): Promise<T> {
  let refreshToken = false;
  for (let attempt = 1; ; attempt++) {
    try {
      return await request(await authHeaders(refreshToken));
    } catch (error) {
      const code = (error as { code?: string })?.code;
      if (code === "nn/auth-expired" && !refreshToken) {
        refreshToken = true;
        continue;
      }
      if (!isRetryable(error) || attempt >= MAX_ATTEMPTS) throw error;
      await wait(Math.min(1000 * 2 ** (attempt - 1), 16_000), signal);
    }
  }
}

export async function resumableUpload(options: ResumableUploadOptions): Promise<ResumableUploadResult> {
  const { storagePath, size, signal } = options;
  const bucket = app.options.storageBucket;
  if (!bucket) throw codedError("storage/no-default-bucket");
  const base = storageBaseUrl(bucket);

  // 1. Start the upload session.
  const uploadUrl = await withRetries(signal, async (headers) => {
    const res = await send(
      `${base}?name=${encodeURIComponent(storagePath)}`,
      {
        ...headers,
        "X-Goog-Upload-Protocol": "resumable",
        "X-Goog-Upload-Command": "start",
        "X-Goog-Upload-Header-Content-Length": String(size),
        "X-Goog-Upload-Header-Content-Type": options.contentType,
        "Content-Type": "application/json; charset=utf-8",
      },
      JSON.stringify({
        name: storagePath,
        contentType: options.contentType,
        cacheControl: options.cacheControl,
        metadata: options.customMetadata,
      }),
      signal,
    );
    checkStatus(res);
    const url = res.header("X-Goog-Upload-URL");
    if (!url) throw codedError("nn/server", "No upload URL");
    return url;
  });

  // 2. Send the file in pieces. After a failure, ask the server how much it
  // already has and continue from there.
  let offset = 0;
  let finalMetadata: { size?: string } | null = null;
  let needsSync = false;
  let failures = 0;
  let refreshToken = false;

  while (!finalMetadata) {
    try {
      const headers = await authHeaders(refreshToken);
      refreshToken = false;
      if (needsSync) {
        const res = await send(uploadUrl, { ...headers, "X-Goog-Upload-Command": "query" }, null, signal);
        checkStatus(res);
        const received = Number(res.header("X-Goog-Upload-Size-Received"));
        if (!Number.isNaN(received)) offset = received;
        needsSync = false;
        if (res.header("X-Goog-Upload-Status") === "final") {
          finalMetadata = safeJson(res.text) ?? { size: String(size) };
          break;
        }
      }

      const start = offset;
      const end = Math.min(start + CHUNK_BYTES, size);
      const chunk = await options.readChunk(start, end);
      if (chunk.size !== end - start) throw codedError("nn/read-failed");
      const res = await send(
        uploadUrl,
        {
          ...headers,
          "X-Goog-Upload-Command": end === size ? "upload, finalize" : "upload",
          "X-Goog-Upload-Offset": String(start),
        },
        chunk,
        signal,
        (loaded) => options.onProgress(start + loaded),
      );
      checkStatus(res);
      const status = res.header("X-Goog-Upload-Status");
      if (status === "final") {
        finalMetadata = safeJson(res.text) ?? {};
      } else if (status !== "active") {
        throw codedError("nn/server", `Unexpected upload status ${status}`);
      }
      offset = end;
      failures = 0;
      options.onProgress(offset);
    } catch (error) {
      const code = (error as { code?: string })?.code;
      if (code === "nn/auth-expired" && !refreshToken) {
        refreshToken = true;
        continue;
      }
      if (!isRetryable(error) || ++failures >= MAX_ATTEMPTS) throw error;
      await wait(Math.min(1000 * 2 ** (failures - 1), 16_000), signal);
      needsSync = true;
    }
  }

  const metadata = finalMetadata as { size?: string };
  return { size: Number(metadata.size ?? NaN) };
}

function safeJson(text: string): { size?: string } | null {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
